import { useEffect } from "react";
import { useLocation } from "wouter";
import metadata from "./siteMetadata.json";

export type SiteMetadata = {
  title: string;
  description: string;
};

export const SITE_METADATA = metadata as Record<string, SiteMetadata>;
export const PUBLIC_SITE_ROUTES = Object.freeze(Object.keys(SITE_METADATA));

const CANONICAL_ORIGIN = "https://envelaro.com";

function normalizedPath(pathname: string) {
  if (!pathname || pathname === "/") return "/";
  return pathname.replace(/\/+$/, "") || "/";
}

function ensureMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([name, value]) => element?.setAttribute(name, value));
    document.head.append(element);
  }
  return element;
}

export function applySiteMetadata(pathname: string) {
  const path = normalizedPath(pathname);
  const routeMetadata = SITE_METADATA[path];
  const canonicalUrl = `${CANONICAL_ORIGIN}${path === "/" ? "/" : path}`;

  if (!routeMetadata) {
    document.title = "Page not found - Envelaro";
    ensureMeta('meta[name="description"]', { name: "description" }).content =
      "The requested Envelaro page could not be found.";
    const robots = ensureMeta('meta[name="robots"]', { name: "robots" });
    robots.content = "noindex, nofollow";
    robots.dataset.siteMetadata = "true";
    return;
  }

  document.title = routeMetadata.title;
  ensureMeta('meta[name="description"]', { name: "description" }).content = routeMetadata.description;
  ensureMeta('meta[property="og:title"]', { property: "og:title" }).content = routeMetadata.title;
  ensureMeta('meta[property="og:description"]', { property: "og:description" }).content = routeMetadata.description;
  ensureMeta('meta[property="og:url"]', { property: "og:url" }).content = canonicalUrl;

  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = canonicalUrl;

  document.head.querySelector<HTMLMetaElement>('meta[name="robots"][data-site-metadata="true"]')?.remove();
}

export function SiteMetadataManager() {
  const [location] = useLocation();

  useEffect(() => {
    applySiteMetadata(location);
  }, [location]);

  return null;
}
